/**
 * GoogleAuthGate — Direct Google OAuth.
 * Redirects to /api/auth/login which handles the Google OAuth flow server-side.
 * Session cookie is set by the backend callback.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '@/config/api';
import { clearOpenFileAutosave } from '@/utils/openFileSession';
import { EvaLogo } from './EvaLogo';

const API = API_BASE_URL;

/* ─── Login Screen ─── */
function LoginScreen() {
  const handleLogin = useCallback(() => {
    window.location.href = `${API}/api/auth/login`;
  }, []);

  return (
    <div className="fixed inset-0 z-[200] bg-gray-100 flex items-center justify-center" data-testid="login-screen">
      <div className="w-[340px] bg-white rounded-lg border border-gray-200 shadow-lg p-6">
        <div className="text-center mb-6">
          <EvaLogo className="h-10 mx-auto mb-4" />
          <h1 className="sr-only" data-testid="login-title">eva</h1>
          <p className="text-[11px] text-gray-500 mt-1">Real estate development analysis</p>
        </div>

        <button onClick={handleLogin}
          className="w-full h-10 bg-white border border-gray-300 hover:bg-gray-50 rounded-md flex items-center justify-center gap-2 transition-colors shadow-sm"
          data-testid="google-login-btn">
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span className="text-[13px] font-medium text-gray-700">Continue with Google</span>
        </button>

        <div className="mt-4 text-center text-[9px] text-gray-400">
          Secured with Google OAuth · restricted access
        </div>
      </div>
    </div>
  );
}

/* ─── User Menu ─── */
export function UserMenu({ user }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } finally {
      // Next login must start as a clean draft (no prior open-file identity / buffer).
      clearOpenFileAutosave();
      window.location.reload();
    }
  }, []);

  if (!user) return null;

  const initials = (user.name || user.email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const shortName = (user.name || user.email || 'User').split(/\s+/)[0].slice(0, 8);

  return (
    <div className="relative" ref={menuRef}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-1 py-1.5 min-h-[28px] rounded hover:bg-gray-100 transition-colors"
        data-testid="user-menu-btn"
        aria-label={`User menu: ${user.name || user.email || 'Account'}`}
        title={user.name || user.email || 'Account'}>
        {user.picture ? (
          <img src={user.picture} alt="" className="w-5 h-5 rounded-full border border-gray-200" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[8px] font-bold flex items-center justify-center">{initials}</div>
        )}
        <span className="hidden md:inline text-[8px] text-gray-600 max-w-[64px] truncate">{shortName}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-testid="user-menu-dropdown">
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-[10px] font-semibold text-gray-800 truncate">{user.name}</div>
            <div className="text-[9px] text-gray-400 truncate">{user.email}</div>
          </div>
          <button onClick={handleLogout}
            className="w-full text-left px-3 py-1.5 text-[10px] text-red-600 hover:bg-red-50 transition-colors"
            data-testid="logout-btn">
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main Gate Component ─── */
export function GoogleAuthGate({ children }) {
  const [state, setState] = useState('loading');
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check for auth error in URL
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError) {
      if (authError === 'unauthorized') {
        setState('unauthorized');
      } else {
        setState('login');
      }
      window.history.replaceState(null, '', window.location.pathname);
      if (authError !== 'unauthorized') {
        console.error('Auth error:', authError);
      }
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const u = await res.json();
          setUser(u);
          setState('authenticated');
        } else {
          setState('login');
        }
      } catch {
        setState('login');
      }
    })();
  }, []);

  if (state === 'loading') return null;
  if (state === 'unauthorized') return (
    <div className="fixed inset-0 z-[200] bg-gray-100 flex items-center justify-center" data-testid="unauthorized-screen">
      <div className="w-[340px] bg-white rounded-lg border border-gray-200 shadow-lg p-6 text-center">
        <EvaLogo className="h-8 mx-auto mb-4" />
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-base font-bold text-gray-900 mb-1">Access Denied</h2>
        <p className="text-[12px] text-gray-500 mb-4">Your email is not authorized to use this application.</p>
        <button onClick={() => { setState('login'); }} className="text-[12px] text-indigo-600 hover:text-indigo-800 font-medium">Try another account</button>
      </div>
    </div>
  );
  if (state === 'login') return <LoginScreen />;

  return typeof children === 'function' ? children(user) : children;
}
